import { useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { Button } from '@/components/Button';
import { LuFolder } from 'react-icons/lu';

type Props = {
  isPending: boolean;
  onFileSelected: (file: File) => Promise<void> | void;
};

export function JobPostImportRequestsUploadSection({ isPending, onFileSelected }: Props) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleSelectFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      await onFileSelected(file);
    } finally {
      // 同じファイルを再度選択できるように value をリセット
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    const file = event.dataTransfer.files?.[0];
    if (!file) return;

    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      return;
    }

    try {
      await onFileSelected(file);
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <>
      <h2 className="text-base font-semibold">CSVファイルをアップロード</h2>

      <div className="mt-md">
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={handleFileChange}
          disabled={isPending}
        />

        {/* ドラッグアンドドロップエリア */}
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`flex h-[224px] w-[812px] flex-col items-center justify-center gap-sm rounded border-2 border-dashed bg-gray-50 p-lg transition-colors ${
            isDragging ? 'border-primary bg-blue-50' : 'border-gray-300'
          } ${isPending ? 'pointer-events-none opacity-50' : ''}`}
        >
          <img src="/agent/candidates/icon-upload.svg" alt="" width={48} height={48} />

          {/* テキスト */}
          <div className="flex flex-col items-center gap-1">
            <p className="text-lg text-gray-700">ここにドラッグアンドドロップ</p>
            <p className="text-base text-gray-700">または</p>
          </div>

          {/* ファイル選択ボタン */}
          <Button
            type="button"
            color="secondary"
            size="md"
            onClick={handleSelectFileClick}
            disabled={isPending}
            className="gap-1"
          >
            <LuFolder size={20} fill="#262626" />
            ファイルを選択
          </Button>
        </div>
      </div>

      <div className="mt-xs flex flex-col gap-xs space-y-1 text-xs text-gray-500">
        <p>※「企業名」「タイトル」をCSVの1行目に含めてください。</p>
        <p>※アップロードできるファイル数は1度に1ファイル、ファイルサイズは5MBまでです。</p>
      </div>
    </>
  );
}
